import * as React from "react";
import { RouteComponentProps } from "react-router-dom";

import { GeographyService } from "@app/geography";
import {
  createRunConfigurationResult, CountyProjectContainer, ProjectOptions, ProjectRunConfigurationResult, ProjectService,
} from "@app/projects";
import { NetworkAssessmentAnalysisType } from "@app/projects/enumerators";
import { AccuracyService } from "@app/projects/network-assessment/accuracy";
import { AdequacyDetail } from "@app/projects/network-assessment/adequacy";
import { AttestationService } from "@app/projects/network-assessment/attestation";
import { AccessSummary } from "@app/projects/results/adequacy";
import { AnalysisSpecialtyGroup, ResultRegion } from "@app/projects/results/common";
import { RegionGranularity } from "@app/region-sets";
import { Resource, ResourceStatus } from "@app/resources";
import { formatDate } from "@app/shared";
import { TemplateService } from "@app/templates";
import { Details } from "./providers/Details";
import { QESSelect } from "@app/shared/qes-standards";
import SpecialtyGroupExceptionsService from "@app/projects/specialtyGroupExceptionsService";

interface ProviderResultsState {
  accessSummary: AccessSummary;
  projectResources: Resource[];
  regionSetId: number;
  runConfiguration: ProjectRunConfigurationResult;
  selectedProject: ProjectOptions;
  specialtyGroupCode: string;
}

export default class ProviderResults extends React.Component<RouteComponentProps<unknown>, ProviderResultsState> {
  private readonly accuracyService = new AccuracyService();
  private readonly attestationService = new AttestationService();
  private readonly geographyService = new GeographyService();
  private readonly projectService = new ProjectService();
  private readonly specialtyGroupExceptionsService = new SpecialtyGroupExceptionsService();
  private readonly templateService = new TemplateService();
  private readonly networkAssessmentAnalysisType = NetworkAssessmentAnalysisType.ServiceArea;

  private runConfigurationMap: { [runConfigurationIdNetworkAssessmentAnalysisTypeKey: string]: ProjectRunConfigurationResult } = {};
  private regionSetsMap = new Map<number, SelectedRegionSet>();

  constructor(props: RouteComponentProps<unknown>) {
    super(props);

    this.state = {
      accessSummary: null,
      projectResources: [],
      regionSetId: null,
      runConfiguration: null,
      selectedProject: null,
      specialtyGroupCode: null,
    };
  }

  public async componentDidMount() {
    // Does not include run configurations.
    const projectResources = await this.projectService.getProjectResources(ResourceStatus.Active);
    this.setState({ projectResources });
  }

  public render() {
    let serviceArea: ResultRegion = null;
    if (this.state.runConfiguration && this.state.runConfiguration.serviceArea) {
      //need to create a new result region of only the state region sets for display
      serviceArea = {
        displayName: this.state.runConfiguration.serviceArea.displayName,
        regionCode: this.state.runConfiguration.serviceArea.regionCode,
        regionGranularity: RegionGranularity.State,
        regionSetId: this.state.runConfiguration.serviceArea.regionSetId,
        subRegions: this.state.runConfiguration.serviceArea.subRegions.filter(
          (resultRegion) => resultRegion.regionGranularity === RegionGranularity.State),
      } as ResultRegion;
    }

    //the specialty groups with provider sets are all the specialty groups available to the template
    //minus any that are excluded
    //(runConfiguration.specialtyGroups is all the specialty group codes available to the template)
    let availableSpecialtyGroups: AnalysisSpecialtyGroup[] = null;
    if (this.state.runConfiguration && this.state.runConfiguration.specialtyGroups) {
      const excludedSpecialtyCodes = this.state.runConfiguration.excludedSpecialtyGroupCodes;
      availableSpecialtyGroups = this.state.runConfiguration.specialtyGroups.filter((item) => !excludedSpecialtyCodes.includes(item.code));
    }

    let key: string = null;
    if (this.state.selectedProject && this.state.runConfiguration && this.state.regionSetId && this.state.specialtyGroupCode) {
      //set a key to re-render the provider panel subcomponents if any of the dropdown values change
      key = `${this.state.selectedProject.projectId}:${this.state.runConfiguration.projectRunConfigurationId}:${this.state.regionSetId}:${this.state.specialtyGroupCode}`;
    }

    return (
      <div className="mb-20">
        <div className="container" >
          <form>
            <div>
              <div className="form-group row">
                <div className="col-6">
                  {this.renderProjectList()}
                </div>
                {this.state.selectedProject && this.state.selectedProject.projectRunConfigurations && this.state.selectedProject.projectRunConfigurations.length > 0 &&
                  <div className="col-6">
                    {this.renderProjectConfigurationsList()}
                  </div>
                }
              </div>
            </div>
            {serviceArea &&
              this.renderResultRegions(serviceArea, 1)
            }
            {availableSpecialtyGroups && availableSpecialtyGroups.length > 0 &&
              this.renderSpecialtyGroupList(availableSpecialtyGroups)
            }
          </form>
        </div >
        {key &&
          <div key={key} className="container">
            <CountyProjectContainer projectId={this.state.selectedProject.projectId}>
              <Details {...this.props} accessSummary={this.state.accessSummary} regionSetId={this.state.regionSetId} specialtyGroupCode={this.state.specialtyGroupCode} />
            </CountyProjectContainer>
          </div>
        }
      </div>
    );
  }

  private renderProjectList() {
    return (
      <QESSelect
        label="Projects"
        className="form-control"
        onChange={(e) => this.handleProjectChange(e.currentTarget.value)}
        testId="provider-results__project-list"
      >
        <option value="">Select One</option>
        {
          this.state.projectResources.map((resource) => {
            const key: number = resource.resourceId;
            return (
              <option key={key} value={key}>
                {resource.name}
              </option>
            );
          })
        }
      </QESSelect>
    );
  }

  private renderProjectConfigurationsList() {
    const selectedValue: string = this.state.runConfiguration ? this.state.runConfiguration.projectRunConfigurationId.toString() : "";

    return (
      <QESSelect
        className="form-control"
        value={selectedValue}
        onChange={(e) => this.handleProjectRunConfigurationsChange(e.currentTarget.value)}
        label="Run Configurations"
        testId="provider-results__project-configuration-list"
      >
        <option value="">Select One</option>
        {
          this.state.selectedProject.projectRunConfigurations.map((config) => {
            const key: number = config.projectRunConfigurationId;
            if (config.datePublished) {
              const date = formatDate(config.datePublished);
              return (
                <option key={key} value={key}>
                  {config.publishComment ? (date + " - " + config.publishComment) : (date)}
                </option>
              );
            } else {
              return (
                <option key={key} value={key} label="Unpublished">
                  Unpublished
                </option>
              );
            }
          })
        });
      </QESSelect>
    );
  }

  private renderResultRegions(resultRegion: ResultRegion, level: number): React.ReactNode {
    return (
      <React.Fragment>
        {
          resultRegion.subRegions && resultRegion.subRegions.length > 0 &&
          this.renderResultRegionList(resultRegion, level)
        }
        {this.regionSetsMap.has(resultRegion.regionSetId) &&
          this.renderResultRegions(this.regionSetsMap.get(resultRegion.regionSetId).subRegion, level + 1)
        }
      </React.Fragment>
    );
  }

  private renderResultRegionList(resultRegion: ResultRegion, level: number) {
    //the granularity should be the same for all the subregions
    const granularity: RegionGranularity = resultRegion.subRegions[0].regionGranularity;

    const selectedValue: string =
      this.regionSetsMap.has(resultRegion.regionSetId) ? this.regionSetsMap.get(resultRegion.regionSetId).subRegion.regionSetId.toString() : "";

    return (
      <div key={resultRegion.regionSetId}>
        <div className="form-group row">
          <div className="col-6">
            <QESSelect
              label={granularity.charAt(0).toUpperCase() + granularity.slice(1)}
              className="form-control"
              value={selectedValue}
              onChange={(e) => this.handleResultRegionChange(resultRegion, e.currentTarget.value, level)}
              testId="provider-results__granularity"
            >
              <option value="">Select One</option>
              {
                resultRegion.subRegions.map((region) => {
                  const key: number = region.regionSetId;
                  return (
                    <option key={key} value={key}>
                      {region.displayName}
                    </option>
                  );
                })
              }
            </QESSelect>
          </div>
        </div>
      </div>
    );
  }

  private renderSpecialtyGroupList(specialtyGroupCodes: AnalysisSpecialtyGroup[]) {
    const selectedValue: string = this.state.specialtyGroupCode ? this.state.specialtyGroupCode : "";

    return (
      <div>
        <div className="form-group row">
          <div className="col-6">
            <QESSelect
              label="Specialty Groups"
              className="form-control"
              value={selectedValue}
              onChange={(e) => this.handleSpecialtyGroupChange(e.currentTarget.value)}
              testId="provider-results__specialty-groups"
            >
              <option value="">Select One</option>
              {
                specialtyGroupCodes.map((specialtyGroup) => {
                  const key: string = specialtyGroup.code;
                  return (
                    <option key={key} value={key}>
                      {specialtyGroup.name}
                    </option>
                  );
                })
              }
            </QESSelect>
          </div>
        </div>
      </div>
    );
  }

  private handleProjectChange = async (projectId: string) => {
    this.regionSetsMap.clear();
    const numProjectId = Number(projectId);

    if (!numProjectId) {
      this.setState({
        accessSummary: null,
        regionSetId: null,
        runConfiguration: null,
        selectedProject: null,
        specialtyGroupCode: null,
      });
      return;
    }

    const selectedProject = (await this.projectService.getById(numProjectId));
    this.setState({
      accessSummary: null,
      regionSetId: null,
      runConfiguration: null,
      selectedProject,
      specialtyGroupCode: null,
    });
  }

  private handleProjectRunConfigurationsChange = async (runConfigurationId: string) => {
    this.regionSetsMap.clear();
    const numRunConfigurationId = Number(runConfigurationId);

    if (!numRunConfigurationId) {
      this.setState({
        accessSummary: null,
        regionSetId: null,
        runConfiguration: null,
        specialtyGroupCode: null,
      });
      return;
    }

    const runKey: string = this.runConfigurationKey(runConfigurationId, this.networkAssessmentAnalysisType);

    let runConfiguration: ProjectRunConfigurationResult = this.runConfigurationMap[runKey];
    if (!runConfiguration) {
      runConfiguration = await createRunConfigurationResult(
        numRunConfigurationId,
        this.state.selectedProject,
        this.projectService,
        this.specialtyGroupExceptionsService,
        this.accuracyService,
        this.attestationService,
        this.templateService,
        this.geographyService,
        this.networkAssessmentAnalysisType);
    }

    this.runConfigurationMap[runKey] = runConfiguration;
    this.setState({
      accessSummary: null,
      regionSetId: null,
      runConfiguration,
      specialtyGroupCode: null,
    });
  }

  private handleResultRegionChange = async (resultRegion: ResultRegion, regionId: string, level: number) => {
    this.removeHigherGranularityRegionSetFromMap(level);
    const numRegionId = Number(regionId);

    if (!numRegionId) {
      this.setState({
        accessSummary: null,
        regionSetId: null,
      });

      return;
    }

    const subRegion: ResultRegion = resultRegion.subRegions.find((r: ResultRegion) => {
      return r.regionSetId === numRegionId;
    });

    this.regionSetsMap.set(resultRegion.regionSetId, new SelectedRegionSet(subRegion, level));

    if (subRegion.subRegions && subRegion.subRegions.length > 0) {
      this.setState({
        accessSummary: null,
        regionSetId: null,
      });
    } else {
      //no more subregions so update state with regionId and accessSummary
      const adequacyDetails: AdequacyDetail[] = await this.state.runConfiguration.getAdequacyDetails(subRegion);
      let accessSummary: AccessSummary = null;
      if (adequacyDetails && adequacyDetails.length > 0) {
        accessSummary = adequacyDetails[0].accessSummary;
      }

      this.setState({
        accessSummary,
        regionSetId: numRegionId,
      });
    }
  }

  private removeHigherGranularityRegionSetFromMap(level: number) {
    const newRegionSetsMap = new Map<number, SelectedRegionSet>();
    //  have to remove all regionSetIds that are at a higher level
    //(higher level meaning they are at a higher granularity
    //and thus a child of the result region being changed)
    //NOTE: If the granularity enum was numeric then it could be
    //used instead of an iterator "level", but it's a string enum.
    for (const entry of this.regionSetsMap.entries()) {
      const key = entry[0];
      const value = entry[1];

      if (value.level < level) {
        newRegionSetsMap.set(key, value);
      }
    }

    this.regionSetsMap = newRegionSetsMap;
  }

  private handleSpecialtyGroupChange = (specialtyGroupId: string) => {
    if (specialtyGroupId === "") {
      this.setState({ specialtyGroupCode: null });
      return;
    }

    this.setState({ specialtyGroupCode: specialtyGroupId });
  }

  private runConfigurationKey(runConfigurationId: string, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): string {
    return `${runConfigurationId}:${networkAssessmentAnalysisType}`;
  }
}

class SelectedRegionSet {
  public readonly subRegion: ResultRegion;
  public readonly level: number;

  constructor(subRegion: ResultRegion, level: number) {
    this.subRegion = subRegion;
    this.level = level;
  }
}
